import slugify from '@sindresorhus/slugify';
import { useCallback, useEffect, useState } from 'preact/hooks';
import { useBeforeunload } from 'react-beforeunload';
import { Accordion } from '../components/accordion';
import { AddRecordToPrWidget } from '../components/add-record-to-pr-widget';
import { FileEditorAccordion } from '../components/file-editor-accordion';
import { FlashMessage, flash } from '../components/flash-message';
import { Link } from '../components/link';
import { useApi } from '../hooks/api';
import { useConfigStore } from '../stores/config';
import { prStore } from '../stores/pr';
import { baseBranch } from '../util/consts';
import { hashNavigate } from '../util/router';

export const CreatePrPage = () => {
    const api = useApi();
    const [pushRepository, pushBranch, commitDirectly] = useConfigStore((s) => [
        s.pushRepository,
        s.pushBranch,
        s.commitDirectly,
    ]);

    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [labels, setLabels] = useState(['record']);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const hasErrors = prStore.useTracked.hasErrors();
    const hasChanges = prStore.useTracked.hasChanges();
    const suggestedTitle = prStore.useTracked.getSuggestedTitle();

    useBeforeunload(hasChanges ? () => 'You have unsaved changes.' : undefined);

    // Reset store on mount
    useEffect(() => {
        prStore.set.reset();
        document.title = 'Create new PR · Datenanfragen.de data editor';
    }, []);

    const handleSubmit = useCallback(async () => {
        setIsSubmitting(true);
        if (!api) {
            flash(<FlashMessage type="error">API not available.</FlashMessage>);
            setIsSubmitting(false);
            return;
        }
        if (!pushRepository) {
            flash(
                <FlashMessage type="error">
                    You need to select a repository to push to in the <Link href="/settings">settings</Link>.
                </FlashMessage>
            );
            setIsSubmitting(false);
            return;
        }
        if (!hasChanges) {
            flash(<FlashMessage type="error">No data to submit. Please add or update a record.</FlashMessage>);
            setIsSubmitting(false);
            return;
        }

        const finalTitle = title || suggestedTitle;

        try {
            if (commitDirectly && (await api.canPushToRepository(api.repository))) {
                await api.commitFilesToBranch({
                    repository: api.repository,
                    branchName: baseBranch,
                    files: prStore.get.toCommitFiles(),
                    commitMessage: finalTitle,
                });
                flash(<FlashMessage type="success">Successfully committed changes to main branch.</FlashMessage>);
                prStore.set.reset();
                hashNavigate('/');
            } else {
                const newBranchName = `data-editor-${slugify(finalTitle).substring(0, 20)}-${Date.now()}`;

                const headBranchFrom = await api.getBranch({ repository: pushRepository, branch: pushBranch });
                if (!headBranchFrom) throw new Error(`Could not get branch '${pushBranch}' from your repository.`);

                await api.createBranch({
                    repository: pushRepository,
                    fromSha: headBranchFrom.commit.sha,
                    newBranchName,
                });

                await api.commitFilesToBranch({
                    repository: pushRepository,
                    branchName: newBranchName,
                    files: prStore.get.toCommitFiles(),
                    commitMessage: finalTitle,
                });

                const newPr = await api.createPullRequest({
                    headRepository: pushRepository,
                    headBranch: newBranchName,
                    baseBranch: baseBranch,
                    title: finalTitle,
                    body: description,
                });

                if (labels.length > 0) await api.addLabelsToPullRequest({ pullRequestNumber: newPr.number, labels });

                flash(<FlashMessage type="success">Successfully created pull request!</FlashMessage>);
                prStore.set.reset();
                hashNavigate(`/review/${newPr.number}`);
            }
        } catch (err) {
            flash(<FlashMessage type="error">An error occurred: {(err as Error).message}</FlashMessage>);
            setIsSubmitting(false);
        }
    }, [api, pushRepository, pushBranch, commitDirectly, hasChanges, title, suggestedTitle, description, labels]);

    if (!pushRepository || !pushBranch)
        return (
            <>
                <div className="alert alert-danger" role="alert">
                    You have not set a repository and/or branch for pushing. Without this, you cannot create pull
                    requests.
                    <br />
                    Please head to the <Link href="/settings">settings page</Link> and specify which repository and
                    branch to use.
                </div>
            </>
        );

    return (
        <>
            <div className="d-flex justify-content-between">
                <h2>Create new PR</h2>

                <div className="d-flex align-items-center">
                    <button
                        className="btn btn-sm mx-1 btn-primary"
                        onClick={handleSubmit}
                        disabled={isSubmitting || !hasChanges || hasErrors}>
                        {isSubmitting ? 'Submitting…' : commitDirectly ? 'Commit changes' : 'Create pull request'}
                    </button>
                </div>
            </div>

            <Accordion header="Pull request details" openInitially={true} className="mb-3">
                <div className="mb-3">
                    <label htmlFor="pr-title" className="form-label">
                        Title
                    </label>
                    <input
                        type="text"
                        className="form-control"
                        id="pr-title"
                        value={title}
                        placeholder={suggestedTitle}
                        onInput={(e) => setTitle(e.currentTarget.value)}
                    />
                </div>
                <div className="mb-3">
                    <label htmlFor="pr-description" className="form-label">
                        Description
                    </label>
                    <textarea
                        className="form-control"
                        id="pr-description"
                        rows={3}
                        value={description}
                        onInput={(e) => setDescription(e.currentTarget.value)}
                    />
                </div>
                <div className="mb-3">
                    <label htmlFor="pr-labels" className="form-label">
                        Labels (comma-separated)
                    </label>
                    <input
                        type="text"
                        className="form-control"
                        id="pr-labels"
                        value={labels.join(', ')}
                        onInput={(e) =>
                            setLabels(
                                e.currentTarget.value
                                    .split(',')
                                    .map((s) => s.trim())
                                    .filter((s) => s)
                            )
                        }
                    />
                </div>
            </Accordion>

            <AddRecordToPrWidget />

            {prStore.useTracked.files().map((file) => (
                <FileEditorAccordion file={file} openInitially={true} showRemove={true} />
            ))}
        </>
    );
};
